import { useState, useMemo, useEffect } from "react";
import type { MentionProvider } from "@lumanity/ui/chat";
import { ChevronRight, FileText, Settings } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { useProjectTemplate } from "@/hooks/useProjects";
import { useAllActiveProjectTemplatesWithPrompts } from "@/hooks/useTemplates";
import { projectsApi } from "@/services/projectsApi";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TemplateSelection } from "./outline/TemplateSelection";
import { OutlineSectionList } from "./outline/SectionList";
import { SectionFilterBar } from "./outline/SectionFilterBar";
import { useExportOutline } from "@/hooks/useExportOutline";
import { buildGridReviewMap } from "@/lib/extractionReview";
import type { Section } from "./outline/SortableRow";
import { withoutPendingSelections } from "@/lib/extractionSelection";

const OutlineCreationView = () => {
  const { projectId, project, projectDocuments, activeStage, handleStageComplete, continueLabel, pageTitle, setChatDocumentIds, setStepStateNote, setMentionProvider } = useProjectDetailContext();
  const { data: activeTemplate, isLoading } = useProjectTemplate(projectId, 'Reporting');
  const { data: allExtractionTemplates = [] } = useAllActiveProjectTemplatesWithPrompts(projectId, 'Extraction');
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const { exportOutline, isExporting } = useExportOutline();

  // Each extraction template becomes a parent node; its prompt_libraries become children —
  // matches ReportWritingView's model so SectionSourceModal groups columns by template correctly.
  const extractionPoints = useMemo(
    () =>
      allExtractionTemplates.flatMap(({ template, prompt_libraries }) => [
        { id: template.id, name: template.name, parent_id: null as string | null },
        ...prompt_libraries.map((pl) => ({ id: pl.id, name: pl.name, parent_id: template.id as string | null })),
      ]),
    [allExtractionTemplates]
  );

  const extractionTemplateIds = useMemo(
    () => allExtractionTemplates.map((t) => t.template.id),
    [allExtractionTemplates]
  );

  const {
    data: extractionDocuments = [],
    isLoading: isExtractionLoading,
    isError: hasExtractionError,
  } = useQuery({
    queryKey: ["projects", projectId, "extraction-documents-all", extractionTemplateIds],
    queryFn: async () => {
      const results = await Promise.allSettled(
        extractionTemplateIds.map((id) => projectsApi.getTemplateDocuments(projectId!, id, true))
      );
      return results.flatMap((r) => {
        if (r.status === "rejected") {
          console.error("Failed to load extraction documents for a template:", r.reason);
          return [];
        }
        // Pending rows are "Select from Evidence Base" picks with no extracted data yet.
        return withoutPendingSelections(r.value);
      });
    },
    enabled: !!projectId && extractionTemplateIds.length > 0,
  });

  // Derived once here so the source modal and the section flag read the same values.
  const gridReview = useMemo(
    () => buildGridReviewMap(extractionDocuments, extractionPoints),
    [extractionDocuments, extractionPoints]
  );

  const { data: artifacts } = useQuery({
    queryKey: ["projects", projectId, "artifacts"],
    queryFn: () => projectsApi.getArtifacts(projectId!),
    enabled: !!projectId,
  });

  const handleExport = async () => {
    if (!activeTemplate) return;
    const pls = activeTemplate.prompt_libraries ?? [];
    const roots = pls.filter((pl) => !pl.parent_id);
    const orderedSections: typeof pls = [];
    for (const root of roots) {
      orderedSections.push(root);
      orderedSections.push(...pls.filter((pl) => pl.parent_id === root.id));
    }
    await exportOutline({
      templateName: activeTemplate.template.name,
      orderedSections,
      projectDocuments,
      project,
    });
  };

  const hasAnyRefinedSection = (activeTemplate?.prompt_libraries ?? []).some(
    (pl) => !!pl.description_ai_output
  );

  // Plain-text summary of outline progress — fed into the chat assistant's system prompt.
  const outlineStateNote = useMemo(() => {
    const sections = activeTemplate?.prompt_libraries ?? [];
    if (sections.length === 0) return null;

    const rootCount = sections.filter((pl) => !pl.parent_id).length;
    const childCount = sections.length - rootCount;
    const refinedCount = sections.filter((pl) => !!pl.description_ai_output).length;
    const missingSourceNames = sections
      .filter((pl) => (pl.sources?.document_ids?.length ?? 0) === 0 && (pl.sources?.extraction_point_ids?.length ?? 0) === 0)
      .map((pl) => pl.name);

    let note = `Outline (${activeTemplate?.template.name ?? "active template"}): ${sections.length} sections` +
      (childCount > 0 ? ` (${rootCount} root, ${childCount} subsections)` : "") +
      `; ${refinedCount} of ${sections.length} have a refined description.`;
    if (missingSourceNames.length > 0) {
      const shown = missingSourceNames.slice(0, 10);
      note += ` Sections missing sources: ${shown.join(", ")}${missingSourceNames.length > 10 ? ", …" : ""}.`;
    }
    return note;
  }, [activeTemplate]);

  useEffect(() => {
    setStepStateNote(outlineStateNote);
    return () => setStepStateNote(null);
  }, [outlineStateNote, setStepStateNote]);

  // Scope the chat assistant to the union of every section's explicitly selected
  // source documents; fall back to all project documents when no section has any
  // configured yet (mirrors ReportWritingView's identical scoping).
  useEffect(() => {
    const projectDocumentIdSet = new Set(projectDocuments.map((d) => d.id));
    const unioned = new Set<string>();
    for (const pl of activeTemplate?.prompt_libraries ?? []) {
      for (const docId of pl.sources?.document_ids ?? []) {
        if (projectDocumentIdSet.has(docId)) unioned.add(docId);
      }
    }
    setChatDocumentIds(unioned.size > 0 ? Array.from(unioned) : null);
  }, [activeTemplate, projectDocuments, setChatDocumentIds]);

  // @-mention provider: resolved fresh at send time (see useAssistantThread in
  // @lumanity/ui), so content is always current — never frozen at mention time.
  const mentionProvider = useMemo<MentionProvider>(() => {
    const sections = activeTemplate?.prompt_libraries ?? [];

    return {
      search: async (query) => {
        const q = query.toLowerCase();
        return sections
          .filter((pl) => pl.name.toLowerCase().includes(q))
          .slice(0, 20)
          .map((pl) => ({ id: pl.id, label: pl.name, description: "Outline section" }));
      },
      resolveContent: (item) => {
        const pl = sections.find((s) => s.id === item.id);
        if (!pl) return item.label;

        const parts = [`Section: ${pl.name}`];
        if (pl.description) {
          parts.push(`Description (what this section should include/exclude): ${pl.description}`);
        }
        if (pl.description_context) {
          parts.push(`Context (background/key messages informing this section): ${pl.description_context}`);
        }
        if (pl.description_ai_output) {
          parts.push(`AI Output (client-facing summary generated from Description + Context): ${pl.description_ai_output}`);
        }
        if (parts.length === 1) {
          parts.push("(no description, context, or AI output yet)");
        }
        return parts.join("\n");
      },
    };
  }, [activeTemplate]);

  useEffect(() => {
    setMentionProvider(mentionProvider);
    return () => setMentionProvider(null);
  }, [mentionProvider, setMentionProvider]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!activeTemplate || showTemplateSelection) {
    return (
      <TemplateSelection
        projectId={projectId}
        projectType={project.projectType}
        onSelected={() => setShowTemplateSelection(false)}
      />
    );
  }

  const sections: Section[] = (() => {
    const pls = activeTemplate.prompt_libraries ?? [];
    const result: Section[] = [];
    for (const root of pls.filter((pl) => !pl.parent_id)) {
      result.push(root);
      result.push(...pls.filter((pl) => pl.parent_id === root.id));
    }
    return result;
  })();

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{pageTitle}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {activeTemplate.template.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowTemplateSelection(true)}>
            <Settings className="w-4 h-4" /> Change Template
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
          >
            <FileText className="w-4 h-4 mr-1.5" />
            {isExporting ? "Exporting…" : "Export Outline"}
          </Button>
          <Button
            variant="brand"
            size="sm"
            onClick={() => handleStageComplete(activeStage)}
            disabled={!hasAnyRefinedSection}
          >
            {continueLabel}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <Settings className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-amber-900 text-sm">Outline Configuration for Client Review</p>
          <p className="text-sm text-amber-800 mt-0.5">
            Configure the report outline, sections, and source list. Use the "Description" field to
            draft a detailed summary explaining what information each section will include/exclude for
            client review.
          </p>
        </div>
      </div>

      <SectionFilterBar
        sections={sections}
        selectedSectionId={selectedSectionId}
        onSelect={setSelectedSectionId}
      />

      <OutlineSectionList
        projectId={projectId}
        activeTemplate={activeTemplate}
        templateQueryKey={["projects", projectId, "template", "Reporting"]}
        documents={projectDocuments}
        extractionPoints={extractionPoints}
        extractionDocuments={extractionDocuments}
        gridReview={gridReview}
        isExtractionLoading={isExtractionLoading}
        hasExtractionError={hasExtractionError}
        filterSectionId={selectedSectionId}
        artifacts={artifacts}
      />
    </Card>
  );
};

export default OutlineCreationView;
